import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { formatTimeAgo, useAutosave } from "@/lib/hooks/use-autosave";
import {
  type CreateProjectInput,
  useAdminProjects,
  useCreateProject,
  useUpdateProject,
} from "@/lib/queries/use-projects";

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export function AdminProjectEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === "new";
  const projectId = isNew ? 0 : parseInt(id || "0", 10);

  const { data: projects } = useAdminProjects();
  const existingProject = projects?.find((p) => p.id === projectId);

  const createProject = useCreateProject();
  const updateProject = useUpdateProject();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [url, setUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [visible, setVisible] = useState(true);
  const [autoSlug, setAutoSlug] = useState(true);

  // Load existing project data
  useEffect(() => {
    if (existingProject) {
      setTitle(existingProject.title);
      setSlug(existingProject.slug);
      setDescription(existingProject.description || "");
      setCategory(existingProject.category || "");
      setUrl(existingProject.url || "");
      setImageUrl(existingProject.image_url || "");
      setDisplayOrder(existingProject.display_order);
      setVisible(existingProject.visible);
      setAutoSlug(false);
    }
  }, [existingProject]);

  // Auto-generate slug from title
  useEffect(() => {
    if (autoSlug && isNew) {
      setSlug(slugify(title));
    }
  }, [title, autoSlug, isNew]);

  // Memoize form data for autosave comparison
  const formData = useMemo(
    () => ({
      title,
      slug,
      description,
      category,
      url,
      imageUrl,
      displayOrder,
      visible,
    }),
    [title, slug, description, category, url, imageUrl, displayOrder, visible]
  );

  // Autosave callback
  const handleAutosave = useCallback(async () => {
    if (!title || !slug || isNew) return; // Don't autosave if required fields are empty or new project

    const projectData: CreateProjectInput = {
      title,
      slug,
      description: description || undefined,
      category: category || undefined,
      url: url || undefined,
      image_url: imageUrl || undefined,
      display_order: displayOrder,
      visible,
    };

    await updateProject.mutateAsync({ id: projectId, ...projectData });
  }, [
    title,
    slug,
    description,
    category,
    url,
    imageUrl,
    displayOrder,
    visible,
    projectId,
    updateProject,
    isNew,
  ]);

  // Autosave hook - only enabled for existing projects
  const {
    lastSaved,
    isSaving: isAutosaving,
    hasUnsavedChanges,
    markAsSaved,
  } = useAutosave({
    data: formData,
    onSave: handleAutosave,
    enabled: !isNew && !!existingProject,
  });

  // Update autosave state when existing project loads
  useEffect(() => {
    if (existingProject && !isNew) {
      markAsSaved();
    }
    // markAsSaved is stable (no deps), safe to exclude
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingProject, isNew]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const projectData: CreateProjectInput = {
      title,
      slug,
      description: description || undefined,
      category: category || undefined,
      url: url || undefined,
      image_url: imageUrl || undefined,
      display_order: displayOrder,
      visible,
    };

    try {
      if (isNew) {
        await createProject.mutateAsync(projectData);
      } else {
        await updateProject.mutateAsync({ id: projectId, ...projectData });
        markAsSaved();
      }
      navigate("/admin/projects");
    } catch {
      // Error handling is done by React Query
    }
  };

  const isSaving = createProject.isPending || updateProject.isPending;

  // Status indicator component
  const SaveStatus = () => {
    if (isNew) return null;

    if (isAutosaving || isSaving) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-yellow-500 animate-pulse" />
          <span className="text-[11px] text-text-muted">Saving...</span>
        </div>
      );
    }

    if (hasUnsavedChanges) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-orange-500" />
          <span className="text-[11px] text-text-muted">Unsaved changes</span>
        </div>
      );
    }

    if (lastSaved) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-green-500" />
          <span className="text-[11px] text-text-muted">Saved {formatTimeAgo(lastSaved)}</span>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <div className="mb-6 md:mb-8">
        <Link
          to="/admin/projects"
          className="inline-block text-[11px] md:text-[12px] text-text-muted hover:text-primary mb-2"
        >
          ← Back to projects
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-[24px] md:text-[28px] font-bold text-primary">
            {isNew ? "New Project" : "Edit Project"}
          </h1>
          <SaveStatus />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl">
        {/* Title */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Project title"
            className="w-full px-4 py-3 bg-card border border-border text-text-primary placeholder:text-text-faint focus:outline-none focus:border-border-hover"
            required
          />
        </div>

        {/* Slug */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Slug
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setAutoSlug(false);
            }}
            placeholder="project-slug"
            className="w-full px-4 py-3 bg-card border border-border text-text-primary placeholder:text-text-faint focus:outline-none focus:border-border-hover"
            required
          />
        </div>

        {/* Category */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Category
          </label>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. YC W24, Side Project, Open Source"
            className="w-full px-4 py-3 bg-card border border-border text-text-primary placeholder:text-text-faint focus:outline-none focus:border-border-hover"
          />
        </div>

        {/* Description */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A short description of the project..."
            rows={3}
            className="w-full px-4 py-3 bg-card border border-border text-text-primary placeholder:text-text-faint focus:outline-none focus:border-border-hover resize-none"
          />
        </div>

        {/* URL */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            URL (optional)
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            className="w-full px-4 py-3 bg-card border border-border text-text-primary placeholder:text-text-faint focus:outline-none focus:border-border-hover"
          />
        </div>

        {/* Image URL */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Image URL (optional)
          </label>
          <input
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="w-full px-4 py-3 bg-card border border-border text-text-primary placeholder:text-text-faint focus:outline-none focus:border-border-hover"
          />
        </div>

        {/* Display Order */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Display Order
          </label>
          <input
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
            className="w-32 px-4 py-3 bg-card border border-border text-text-primary focus:outline-none focus:border-border-hover"
          />
          <p className="text-[11px] text-text-faint mt-1">Lower numbers appear first</p>
        </div>

        {/* Visible Toggle */}
        <div className="mb-8">
          <label className="flex items-center gap-3 cursor-pointer py-2">
            <input
              type="checkbox"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
              className="w-5 h-5 accent-primary"
            />
            <span className="text-[14px] text-text-secondary">Show on website</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-4">
          <button
            type="submit"
            disabled={isSaving}
            className="w-full md:w-auto px-6 py-3 bg-primary text-primary-foreground font-bold text-[14px] hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {isSaving ? "Saving..." : isNew ? "Create Project" : "Save Changes"}
          </button>
          <Link
            to="/admin/projects"
            className="w-full md:w-auto px-6 py-3 border border-border text-text-secondary font-bold text-[14px] hover:border-border-hover transition-colors text-center"
          >
            Cancel
          </Link>
        </div>

        {(createProject.error || updateProject.error) && (
          <p className="text-destructive text-[13px] mt-4">
            Failed to save project. Please try again.
          </p>
        )}
      </form>
    </div>
  );
}
